import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import {
  getOpdLabTestStatus,
  getLabTestName,
  isOpdResultEditable,
  normalizeTestName,
  buildReportsFromPrescription,
  resolvePrescriptionReports,
  getLabTestCatalogMaps,
  resolveReportTestNameFromCatalog,
} from "../labTestWorkflowUtils";
import { getBoundPathLabReportId, getLabTestCatalogId, getPathLabReportId } from "../../../utils/labTestDedupeUtils";
import OpdTestResultsPanel from "./OpdTestResultsPanel";
import WorkflowPanelModal from "./WorkflowPanelModal";
import { OpdWorkflowDetails } from "./WorkflowPatientDetails";

const normalizeReportsWithCatalog = (reports, catalogById, catalogByName) =>
  (Array.isArray(reports) ? reports : []).map((report) => {
    const catalogId = getLabTestCatalogId(report);
    // Keep API/report test name authoritative so a stale catalog id cannot rename
    // CBC → Thyroid (and cause updateReportResult to hit the wrong reportId).
    const apiName =
      report?.testName || report?.test_name || report?.labTestName || report?.name || "";
    const catalogName = resolveReportTestNameFromCatalog(report, catalogById, catalogByName);
    return {
      ...report,
      testId: catalogId ?? report?.testId ?? report?.labTestId ?? null,
      labTestId: catalogId ?? report?.labTestId ?? report?.testId ?? null,
      testName: String(apiName).trim() || catalogName || "",
    };
  });

const namesMatchFocus = (labTest, focusTestName) => {
  if (!focusTestName) return false;
  const target = normalizeTestName(focusTestName);
  const names = [
    getLabTestName(labTest),
    labTest?.testName,
    labTest?.test_name,
    labTest?.name,
    labTest?.labTestName,
  ]
    .filter(Boolean)
    .map((value) => normalizeTestName(value));
  return names.some((name) => name === target);
};

const matchesFocusLabTest = (labTest, prescription) => {
  const focusReportId = prescription?._focusReportId;
  const focusLabTestId = prescription?._focusLabTestId;
  const focusTestName = prescription?._focusTestName;

  if (focusReportId != null && focusReportId !== "") {
    const labReportId = getBoundPathLabReportId(labTest);
    if (labReportId != null && Number(labReportId) === Number(focusReportId)) {
      return true;
    }
  }

  if (focusLabTestId != null && focusLabTestId !== "") {
    const catalogId = getLabTestCatalogId(labTest);
    if (catalogId != null && Number(catalogId) === Number(focusLabTestId)) {
      // When a specific report is focused, only accept catalog match if names also agree
      // (or lab test is unbound) — avoids opening Urine form for a Typhoid reportId.
      if (focusTestName && !namesMatchFocus(labTest, focusTestName)) {
        return false;
      }
      return true;
    }
  }

  return namesMatchFocus(labTest, focusTestName);
};

/** Prefer exact reportId; else one unbound name/catalog match. Never rebind a different test. */
const resolveFocusedEditableTests = (editable, prescription) => {
  const focusReportId = prescription?._focusReportId;
  const focusLabTestId = prescription?._focusLabTestId;
  const focusTestName = prescription?._focusTestName;

  if (focusReportId == null || focusReportId === "") {
    const focused = editable.filter((test) => matchesFocusLabTest(test, prescription));
    return focused.length > 0 ? focused : editable;
  }

  const byReportId = editable.filter((test) => {
    const reportId = getBoundPathLabReportId(test);
    return reportId != null && Number(reportId) === Number(focusReportId);
  });
  if (byReportId.length > 0) return byReportId;

  const byNameOrCatalog = editable.filter((test) => {
    if (focusTestName && namesMatchFocus(test, focusTestName)) {
      if (focusLabTestId != null && focusLabTestId !== "") {
        const catalogId = getLabTestCatalogId(test);
        if (catalogId != null && Number(catalogId) !== Number(focusLabTestId)) {
          // Name wins when catalog disagrees — still require name match above.
        }
      }
      return true;
    }
    if (focusLabTestId != null && focusLabTestId !== "") {
      const catalogId = getLabTestCatalogId(test);
      if (catalogId != null && Number(catalogId) === Number(focusLabTestId)) {
        // Catalog-only fallback only when focus name is missing or agrees.
        if (!focusTestName || namesMatchFocus(test, focusTestName)) return true;
      }
    }
    return false;
  });

  // Prefer rows not already tied to a different report date.
  const unbound = byNameOrCatalog.filter((test) => {
    const reportId = getBoundPathLabReportId(test);
    return reportId == null || Number(reportId) === Number(focusReportId);
  });
  if (unbound.length > 0) return unbound.slice(0, 1);

  // Same catalog/name may be stamped with another date's reportId after enrich.
  // Still open the matching test template; the panel binds values via focusReportId
  // only when report identity matches (never cross-test).
  if (byNameOrCatalog.length > 0 && focusTestName) {
    return byNameOrCatalog.slice(0, 1);
  }
  return [];
};

const OpdTestResultsModal = ({ open, onClose, prescription, onSubmitted, onSubmittingChange }) => {
  const [reports, setReports] = useState(() => buildReportsFromPrescription(prescription));

  const loadReports = async () => {
    if (!prescription?.patientId) return;
    try {
      const { catalogById, catalogByName } = await getLabTestCatalogMaps();
      const embedded = normalizeReportsWithCatalog(
        buildReportsFromPrescription(prescription),
        catalogById,
        catalogByName
      );

      // Always refresh from patient/API and merge onto embedded so edit forms
      // see the latest COMPLETE results (not only stale prescription embeds).
      const data = await resolvePrescriptionReports(prescription);
      const merged = normalizeReportsWithCatalog(
        data?.length ? data : embedded,
        catalogById,
        catalogByName
      );
      setReports(merged.length ? merged : embedded);
    } catch (error) {
      console.error("[OpdTestResultsModal] Failed to load reports:", error);
      setReports(buildReportsFromPrescription(prescription));
    }
  };

  useEffect(() => {
    if (open) {
      loadReports();
    }
  }, [open, prescription?.patientId, prescription?.id, prescription?.patientLabTests]);

  const labTests = prescription?.patientLabTests || [];
  const hasFocus =
    prescription?._focusTestName ||
    prescription?._focusReportId != null ||
    prescription?._focusLabTestId != null;

  const editableTests = useMemo(() => {
    const editable = labTests.filter(isOpdResultEditable);
    if (!hasFocus) return editable;
    return resolveFocusedEditableTests(editable, prescription);
  }, [labTests, prescription, hasFocus]);

  // Reset panel form when opening a different report row (same test, different date).
  const resultsPanelKey = [
    prescription?.id,
    prescription?._focusReportId,
    prescription?._focusLabTestId,
    prescription?._focusTestName,
  ].join(":");

  // Only auto-close when there is truly nothing to edit (no focus fallback possible).
  useEffect(() => {
    if (!open || !prescription) return;
    if (editableTests.length > 0) return;
    if (hasFocus) return;
    onClose && onClose();
  }, [open, prescription, editableTests.length, hasFocus, onClose]);

  const handleSubmitted = async (saved) => {
    await loadReports();
    if (onSubmitted) await onSubmitted(saved);
  };

  const detailsPrescription = editableTests.length
    ? { ...prescription, patientLabTests: editableTests }
    : prescription;
  const modalTitle =
    editableTests.length > 0 &&
    editableTests.every((test) => getOpdLabTestStatus(test) === "COMPLETE")
      ? "Edit Results"
      : "Add Results";

  if (!prescription || !open) return null;

  return (
    <WorkflowPanelModal open={open} onClose={onClose} title={modalTitle} width={{ xs: "95%", sm: 680 }}>
      <OpdWorkflowDetails prescription={detailsPrescription} />
      {editableTests.length > 0 ? (
        <OpdTestResultsPanel
          key={resultsPanelKey}
          prescription={prescription}
          reports={reports}
          focusTestName={prescription?._focusTestName}
          focusReportId={prescription?._focusReportId}
          focusLabTestId={prescription?._focusLabTestId}
          onSubmitted={handleSubmitted}
          onSubmittingChange={onSubmittingChange}
          embedded
        />
      ) : (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {hasFocus
            ? "Could not safely link this report to a lab test. Please refresh the list and try again."
            : "No editable tests found for this prescription."}
        </Typography>
      )}
    </WorkflowPanelModal>
  );
};

export default OpdTestResultsModal;
